import axios from 'axios'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { BASE_URL, authToken } from '../../../../../../config/constants'
import { Profitraining } from '../types/profitrainings'
import moment from 'moment'
import { PaidSubscription } from '../types/users'

interface ProfitrainingCheckInData {
    profitrainingId: number
    singleProfitrainingId: number
    userId: number
    presence: boolean
}

interface ProfitrainingsProps {
    profitrainings: Profitraining[] | null
    setProfitrainings: React.Dispatch<
        React.SetStateAction<Profitraining[] | null>
    >
    fetchProfitrainings: () => Promise<void>
    revalidateProfitrainings: () => Promise<void>
    isLoadingProfitrainings: boolean
    isRevalidatingProfitrainings: boolean
    errorProfitrainings: string | null
    selectedDay: moment.Moment | null
    setSelectedDay: (day: moment.Moment | null) => void
    updateProfitraining: (
        id: number,
        updatedProfitraining: any,
        updatedPaidSubscription?: PaidSubscription
    ) => Promise<void>
    checkInProfitraining: (data: ProfitrainingCheckInData) => Promise<any>
}

const ProfitrainingsContext = createContext<ProfitrainingsProps | undefined>(
    undefined
)

export const ProfitrainingsProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [profitrainings, setProfitrainings] = useState<
        Profitraining[] | null
    >(null)
    const [isLoadingProfitrainings, setIsLoadingProfitrainings] =
        useState(false)
    const [isRevalidatingProfitrainings, setIsRevalidatingProfitrainings] =
        useState(false)
    const [errorProfitrainings, setErrorProfitrainings] = useState<
        string | null
    >(null)
    const [selectedDay, setSelectedDay] = useState<moment.Moment | null>(
        moment()
    )
    const profitrainingsRequestRef = useRef<Promise<void> | null>(null)

    const loadProfitrainings = async (background = false) => {
        if (profitrainingsRequestRef.current) {
            return profitrainingsRequestRef.current
        }

        if (background) {
            setIsRevalidatingProfitrainings(true)
        } else {
            setIsLoadingProfitrainings(true)
        }
        setErrorProfitrainings(null)

        const request = (async () => {
            const response = await axios.get(
                `${BASE_URL}/api/profitrainings?populate[single_profitraining]=*`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )
            setProfitrainings(response.data.data)
        })()

        profitrainingsRequestRef.current = request

        try {
            await request
        } catch (error: unknown) {
            console.error(
                background
                    ? 'Error revalidating profitrainings:'
                    : 'Error fetching profitrainings:',
                error
            )
            if (!background) {
                setErrorProfitrainings('Failed to fetch profitrainings.')
            }
        } finally {
            profitrainingsRequestRef.current = null
            if (background) {
                setIsRevalidatingProfitrainings(false)
            } else {
                setIsLoadingProfitrainings(false)
            }
        }
    }

    const fetchProfitrainings = async () => {
        await loadProfitrainings(false)
    }

    const revalidateProfitrainings = async () => {
        await loadProfitrainings(profitrainings !== null)
    }

    const updateProfitraining = async (
        id: number,
        updatedData: any,
        updatedPaidSubscription?: PaidSubscription
    ) => {
        setIsLoadingProfitrainings(true)
        setErrorProfitrainings(null)

        try {
            const responses = await Promise.all(
                [
                    axios.put(
                        `${BASE_URL}/api/profitrainings/${id}?populate[single_profitraining]=*`,
                        { data: updatedData },
                        {
                            headers: { Authorization: `Bearer ${authToken}` },
                        }
                    ),
                    updatedPaidSubscription &&
                        axios.put(
                            `${BASE_URL}/api/paid-subscriptions/${updatedPaidSubscription.id}`,
                            { data: updatedPaidSubscription },
                            {
                                headers: {
                                    Authorization: `Bearer ${authToken}`,
                                },
                            }
                        ),
                ].filter(Boolean)
            )

            if (responses[0]?.data) {
                return responses[0].data
            }
        } catch (error: any) {
            console.error(
                'Error updating profitraining:',
                error.response?.data || error.message
            )
            setErrorProfitrainings('Failed to update profitraining.')
        } finally {
            setIsLoadingProfitrainings(false)
        }
    }

    const checkInProfitraining = async (data: ProfitrainingCheckInData) => {
        setIsLoadingProfitrainings(true)
        setErrorProfitrainings(null)

        try {
            const response = await axios.post(
                `${BASE_URL}/api/check-in/profitraining`,
                data,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )

            const updatedSingleProfitrainings =
                response.data?.single_profitraining

            if (updatedSingleProfitrainings) {
                setProfitrainings(
                    (prev) =>
                        prev?.map((p) =>
                            p.id === data.profitrainingId
                                ? {
                                      ...p,
                                      attributes: {
                                          ...p.attributes,
                                          single_profitraining:
                                              updatedSingleProfitrainings,
                                      },
                                  }
                                : p
                        ) ?? null
                )
            }

            return response.data
        } catch (error: any) {
            console.error(
                'Error during profitraining check-in:',
                error.response?.data || error.message
            )
            setErrorProfitrainings('Failed to process check-in.')
            throw error
        } finally {
            setIsLoadingProfitrainings(false)
        }
    }

    useEffect(() => {
        fetchProfitrainings()
    }, [])

    return (
        <ProfitrainingsContext.Provider
            value={{
                profitrainings,
                setProfitrainings,
                fetchProfitrainings,
                revalidateProfitrainings,
                isLoadingProfitrainings,
                isRevalidatingProfitrainings,
                errorProfitrainings,
                selectedDay,
                setSelectedDay,
                updateProfitraining,
                checkInProfitraining,
            }}
        >
            {children}
        </ProfitrainingsContext.Provider>
    )
}

export const useProfitrainings = () => {
    const context = useContext(ProfitrainingsContext)
    if (!context) {
        throw new Error(
            'useProfitrainings must be used within a ProfitrainingsProvider'
        )
    }
    return context
}
